<template>
    <div class="p-6">
        <h1 class="text-2xl font-medium text-gray-800 mb-8">Change Password</h1>

        <div class="flex items-start gap-3 p-4 bg-white border border-orange-200 rounded-lg mb-8">
            <div class="flex items-center justify-center flex-shrink-0 mt-0.5">
                <component :is="Info" class="w-6 h-6 text-primary"/>
            </div>
            <p class="text-gray-600 text-sm leading-relaxed">
                Choose your password, containing a minimum of 8 characters, including a capital letter, a number and a special character (!, ?, #, $, *, %).
            </p>
        </div>

        <!-- Steps -->
        <div class="flex justify-between items-center border-b border-gray-200 mb-8">
            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
                <div
                    :class="['w-8 h-8 flex items-center justify-center rounded-full font-bold z-10', activeStep === 1 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                    1
                </div>
                <span
                    :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 1 }]">Fill Data</span>
                <!--                <div class="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 z-0"></div>-->
            </div>

            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
                <div
                    :class="['w-8 h-8 flex items-center justify-center rounded-full z-10', activeStep === 2 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                    2
                </div>
                <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 2 }]">Check the Operation</span>
            </div>

            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
                <div
                    :class="['w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-700 rounded-full z-10', activeStep === 3 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                    3
                </div>
                <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 3 }]">Confirmation</span>
            </div>
        </div>

        <div class="space-y-6">
            <div>
                <div class="relative">
                    <input
                        v-model="currentPassword"
                        :type="showCurrentPassword ? 'text' : 'password'"
                        placeholder="Current password *"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
                    />
                    <button
                        @click="showCurrentPassword = !showCurrentPassword"
                        type="button"
                        class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path v-if="!showCurrentPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path v-if="!showCurrentPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            <path v-if="showCurrentPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                    </button>
                </div>
            </div>

            <div>
                <div class="relative">
                    <input
                        v-model="newPassword"
                        :type="showNewPassword ? 'text' : 'password'"
                        placeholder="New password *"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
                    />
                    <button
                        @click="showNewPassword = !showNewPassword"
                        type="button"
                        class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path v-if="!showNewPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path v-if="!showNewPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            <path v-if="showNewPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                    </button>
                </div>
                <p class="text-sm text-gray-600 mt-2">
                    The password must have at least 8 characters, including a lowercase letter, an uppercase letter, a digit, and a special character ($, @, !, %, *, ?, &, #)
                </p>
            </div>

            <div>
                <div class="relative">
                    <input
                        v-model="confirmPassword"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        placeholder="Re-enter new password *"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
                    />
                    <button
                        @click="showConfirmPassword = !showConfirmPassword"
                        type="button"
                        class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path v-if="!showConfirmPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path v-if="!showConfirmPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            <path v-if="showConfirmPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <div class="flex justify-center mt-8">
            <button
                @click.prevent="handleChangePassword"
                class="bg-primary text-white font-medium py-3 px-12 rounded-lg transition-colors duration-200">
                CONFIRM
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Info } from 'lucide-vue-next';
import api from '@/services/api'

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const activeStep = ref(1);

// const handleConfirm = () => {
//     // Handle form submission logic here
//     console.log('Form submitted:', {
//         currentPassword: currentPassword.value,
//         newPassword: newPassword.value,
//         confirmPassword: confirmPassword.value
//     })
// }


async function handleChangePassword() {
  try {
    const token = localStorage.getItem('token')
    console.log(token);

    const res = await api.post(
        '/change-password',
        {
          current_password: currentPassword.value,
          new_password: newPassword.value,
          confirm_password: confirmPassword.value,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
    )

    console.log(res.data);

    alert(res.data.message)
    activeStep.value = 3 // رفتن به مرحله Confirmation
  } catch (err) {
    console.error("Change password error:", err.response?.data || err)
    alert("Password change failed: " + (err.response?.data?.message || 'Unknown error'))
  }
}
</script>